import React from 'react';
import { Link } from 'react-router-dom';
import { IndianRupee, CheckCircle, TrendingUp, Shield, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import SEO from '@/components/SEO';
import { DASHBOARD_SIGNIN_URL } from '@/config/links';

const CODPage = () => {
  const benefits = [
    {
      icon: IndianRupee,
      title: 'Flexible Payment',
      description: 'Let your customers pay when they receive their order, increasing trust and conversion rates.'
    },
    {
      icon: TrendingUp,
      title: 'Boost Sales',
      description: 'COD options can increase your sales by 40-60% by reducing purchase hesitation.'
    },
    {
      icon: Shield,
      title: 'Secure & Reliable',
      description: 'We ensure safe collection and prompt remittance of COD amounts to your account.'
    },
    {
      icon: CheckCircle,
      title: 'Easy Reconciliation',
      description: 'Track all COD payments with automated reconciliation and detailed reports.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Cash on Delivery for eCommerce"
        description="Offer cash on delivery with secure COD collection, transparent remittance, and fraud prevention from Boxd Logistics."
        keywords="cash on delivery, cod shipping, ecommerce cod, cod remittance india"
        canonical="https://boxdlogistics.in/cod"
      />
      <Navbar />
      
      {/* Hero — layered background (aligned with Pricing page) */}
      <section className="relative pt-28 pb-20 md:pt-32 md:pb-24 px-4 overflow-hidden bg-gradient-to-b from-blue-100/40 via-blue-50/60 to-white">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-transparent to-blue-50/80 pointer-events-none" />
        <div
          className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[min(120vw,900px)] h-[480px] rounded-full pointer-events-none opacity-90"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(59, 130, 246, 0.22) 0%, rgba(219, 234, 254, 0.35) 35%, transparent 70%)'
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.65]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(59, 130, 246, 0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.06) 1px, transparent 1px)',
            backgroundSize: '48px 48px'
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            backgroundImage:
              'linear-gradient(rgba(30, 64, 175, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(30, 64, 175, 0.04) 1px, transparent 1px)',
            backgroundSize: '96px 96px'
          }}
        />
        <div className="absolute -top-20 -right-20 w-80 h-80 md:w-[28rem] md:h-[28rem] rounded-full bg-gradient-to-br from-blue-300/30 to-blue-700/10 blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -left-24 w-72 h-72 md:w-96 md:h-96 rounded-full bg-gradient-to-tr from-emerald-200/25 to-blue-200/20 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 rounded-full bg-gradient-to-t from-blue-500/15 to-transparent blur-2xl pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-300/50 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-200/40 to-transparent pointer-events-none" />

        <div className="relative max-w-5xl mx-auto text-center z-10">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-700 to-blue-800 rounded-full mb-6 shadow-lg shadow-blue-700/25">
            <IndianRupee className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-blue-800 to-blue-950 bg-clip-text text-transparent">
              Cash on Delivery (COD)
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Offer your customers the convenience of paying when they receive their order. 
            Build trust and increase sales with our reliable COD service.
          </p>
          
          <Button 
            size="lg"
            className="bg-gradient-to-r from-blue-800 to-blue-900 hover:from-blue-900 hover:to-blue-950 text-white px-10 py-7 text-xl rounded-xl shadow-lg shadow-blue-800/25"
            onClick={() => { window.location.href = DASHBOARD_SIGNIN_URL; }}
          >
            Enable COD Now
          </Button>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-blue-800 font-semibold text-sm uppercase tracking-wide">Why Choose COD</span>
            <h2 className="text-4xl font-bold mt-2 mb-4 text-gray-900">
              Benefits of <span className="text-blue-800">Cash on Delivery</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index}
                  className="relative group p-6 bg-white rounded-2xl border-2 border-gray-200 hover:border-blue-500 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-700 to-blue-800 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              How COD <span className="text-blue-800">Works</span>
            </h2>
          </div>
          
          <div className="space-y-6">
            {[
              { step: '1', title: 'Customer Places Order', desc: 'Customer selects COD as payment method at checkout' },
              { step: '2', title: 'Order Shipped', desc: 'We ship the order and collect payment upon delivery' },
              { step: '3', title: 'Payment Collected', desc: 'Our delivery partner collects cash from customer' },
              { step: '4', title: 'Amount Remitted', desc: 'COD amount is remitted to your account within 7 days' }
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-6 bg-white p-6 rounded-2xl border-2 border-gray-200">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-700 to-blue-800 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">{item.step}</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — same layout as homepage FinalCTA (Supercharge section) */}
      <section className="relative py-32 bg-gradient-to-br from-blue-800 via-blue-900 to-blue-950 overflow-hidden">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255, 255, 255, 0.05) 2px, transparent 2px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 2px, transparent 2px)',
              backgroundSize: '60px 60px',
              animation: 'gridMove 20s linear infinite'
            }}
          />
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" />
          <div
            className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-white/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: '1s' }}
          />
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: '2s' }}
          />
          <div
            className="absolute top-20 right-20 w-32 h-32 border-2 border-white/20 rounded-lg rotate-12 animate-spin"
            style={{ animationDuration: '20s' }}
          />
          <div
            className="absolute bottom-20 left-20 w-24 h-24 border-2 border-white/20 rounded-full animate-bounce"
            style={{ animationDuration: '3s' }}
          />
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-semibold">Cash on Delivery</span>
          </div>

          <h2 className="text-5xl md:text-6xl font-bold mb-6 text-white leading-tight">
            Supercharge Your eCommerce
            <br />
            Deliveries with Boxd Logistics!
          </h2>

          <p className="text-xl md:text-2xl text-blue-100 mb-12 max-w-3xl mx-auto leading-relaxed">
            Enable COD alongside the rest of our shipping stack—accelerate growth with seamless fulfilment, collections,
            and reconciliation. Simplified logistics, happy customers.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
            <Button
              size="lg"
              className="bg-white text-blue-900 hover:bg-gray-100 px-10 py-7 text-xl font-bold shadow-2xl"
              onClick={() => { window.location.href = DASHBOARD_SIGNIN_URL; }}
            >
              Start Now <ArrowRight className="ml-2 w-6 h-6" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 px-10 py-7 text-xl font-bold backdrop-blur-sm"
              asChild
            >
              <Link to="/contact">Contact Sales</Link>
            </Button>
          </div>

          <p className="text-blue-100 text-sm">No commitments, instant access to powerful tools!</p>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default CODPage;